import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'

import { useUnit } from 'effector-react'

import { LoadingBar } from '@/components/ui/LoadingBar.tsx'
import { AudioProvider } from '@/components/ui/audio-player/AudioProvider'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import {
  $canLoadNextMessagePages,
  $groupedPinnedMessagesEntries,
  $pinnedMessageIdToFetch,
  $pinnedMessages,
  getNextPinnedMessagePageFx,
  getPinnedMessagesFx,
  setPinnedMessageIdToFetch,
} from '@/models/chattingUI/messages/pinnedMessages.ts'
import {
  $searchResultIndex,
  $searchedMessageIds,
  searchPinnedTextFx,
  setSearch,
} from '@/models/chattingUI/messages/pinnedMessagesSearch.ts'
import { ChatMessageGroup } from '@/modules/chatting/ui/components/ChatMessageGroup/ChatMessageGroup'
import { MessagesSkeleton } from '@/modules/chatting/ui/components/MessagesSkeleton'
import { SearchField } from '@/modules/chatting/ui/components/search-field'

import { DialogProps } from '../types'

export const PinnedMessagesDialog = ({ isOpen, onClose }: DialogProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const previousScrollHeight = useRef<number>(0)
  const anchorRef = useRef<HTMLDivElement>(null)

  const messages = useUnit($pinnedMessages)
  const groupedMessagesEntries = useUnit($groupedPinnedMessagesEntries)
  const isLoading = useUnit(getPinnedMessagesFx.pending)
  const hasNextPage = useUnit($canLoadNextMessagePages)
  const isSearching = useUnit(searchPinnedTextFx.pending)
  const searchedMessageIds = useUnit($searchedMessageIds)
  const searchResultIndex = useUnit($searchResultIndex)
  const pinnedMessageIdToFetch = useUnit($pinnedMessageIdToFetch)
  const [searchString, setSearchString] = useState('')
  const [scrolled, setScrolled] = useState(false)

  useLayoutEffect(() => {
    if (!isLoading && messages.length) {
      if (containerRef.current && previousScrollHeight.current) {
        containerRef.current.scrollTop =
          containerRef.current.scrollHeight - previousScrollHeight.current

        previousScrollHeight.current = 0
      }
    }
  }, [isLoading, messages.length])

  // Scroll to the bottom when the component first mounts
  useEffect(() => {
    if (!scrolled && containerRef.current && !isLoading && messages.length) {
      setScrolled(true)
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [isLoading, messages.length, scrolled])

  // Detect when the user scrolls to the top
  useEffect(() => {
    const container = containerRef.current

    if (container) {
      const handleScroll = () => {
        if (
          containerRef.current &&
          containerRef.current.scrollTop < 200 &&
          !isLoading &&
          hasNextPage
        ) {
          // Save the scroll height before loading new messages
          previousScrollHeight.current = containerRef.current.scrollHeight
          getNextPinnedMessagePageFx()
        }
      }

      container.addEventListener('scroll', handleScroll)

      return () => container.removeEventListener('scroll', handleScroll)
    }
  }, [isLoading, hasNextPage])

  const highlightMessage = useCallback((messageId: number) => {
    const element = containerRef.current?.querySelector(
      `#chatting-message-${messageId}`,
    )

    if (element) {
      element.classList.remove('animate-pulse', 'repeat-1')
      element.scrollIntoView()
      element.classList.add('animate-pulse', 'repeat-1')
    }
  }, [])

  useEffect(() => {
    if (pinnedMessageIdToFetch && !isSearching) {
      highlightMessage(pinnedMessageIdToFetch)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedMessageIdToFetch, highlightMessage])

  const handleSearchChange = (value: string) => {
    setSearchString(value)
    setSearch(value)
  }

  return (
    <Dialog
      onClose={onClose}
      panelClassName="mt-0 sm:mt-0 bg-background"
      bigDialog
      childrenClassName="overflow-hidden"
      show={isOpen}
    >
      <Dialog.Header>
        <Dialog.Title>Chat Pinned Messages</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description>
        <div className="text-text-default relative py-6">
          <div className="flex items-center pb-3">
            <SearchField
              isSearching={isSearching}
              results={searchedMessageIds}
              handleSearchChange={handleSearchChange}
              value={searchString}
              shownResultIndex={searchResultIndex}
              onNext={setPinnedMessageIdToFetch}
              onPrev={setPinnedMessageIdToFetch}
            />
          </div>
          {isLoading && <LoadingBar />}
          <div
            className="max-h-[50vh] min-h-40 overflow-auto py-6 pr-1"
            ref={containerRef}
          >
            {messages.length ? (
              <AudioProvider>
                {groupedMessagesEntries.map(([date, msgs]) => (
                  <ChatMessageGroup
                    date={date}
                    messages={msgs}
                    key={date}
                    highlightMessage={highlightMessage}
                    searchedMessageId={pinnedMessageIdToFetch ?? null}
                    searchedKeyword={searchString}
                    onClickReply={onClose}
                  />
                ))}
              </AudioProvider>
            ) : isLoading ? (
              <MessagesSkeleton />
            ) : (
              <div className="col-span-full flex h-20 items-center justify-center">
                <p className="text-neutral-500">No pinned message found</p>
              </div>
            )}
            <div
              ref={anchorRef}
              className="overflow-anchoring-auto h-[1px]"
            />
          </div>
        </div>
      </Dialog.Description>
      <Dialog.Footer>
        <Button
          onClick={onClose}
          variant="outline"
          className="bg-background w-full"
        >
          Close
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
